import { ToastProvider } from '@enter/core';
import { InactivityDetector, ProviderMessagingProvider } from 'components';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RestrictedRoute } from 'router';
import { DialogProvider } from 'services';
import { IInjectedRootStore } from 'stores';
import { ChargemasterDetail, ChargemasterListing } from './chargemasters';
import { ByFilter, ClaimDetail, ClaimEditor, ClaimListing } from './claims';
import { Documents } from './documents';
import { Eligibility } from './eligibility';
import { Fax } from './fax';
import { GuarantorDetails, Guarantors } from './guarantors';
import { Mail } from './mail';
import { MainSidebar } from './main.sidebar';
import * as S from './main.styles';
import { MainTopnav } from './main.topnav';
import { MessagingOutlet } from './messaging';
import {PatientStatement, StatementDetails} from "./patients";
import { Payments } from './payments';
import { Overpayments } from './overpayments';
import { Reports } from './reports';
import { Settings } from './settings';
import { Settlements } from './settlements';
import { Transactions } from './transactions';
import {Phone} from "./phone";
import { ExperimentsOutlet, EvidenceOutlet } from './verifications';

type IMainProps = IInjectedRootStore;

export const Main: React.FunctionComponent<IMainProps> = inject('rootStore')(
    observer(
        (props: IMainProps): React.ReactElement => {
            const { rootStore } = props;

            const renderNotFound = (): React.ReactElement => <Navigate to="/404" replace />;

            return (
                <ToastProvider>
                    <DialogProvider>
                        <ProviderMessagingProvider>
                            <MainSidebar rootStore={rootStore} />
                            <MainTopnav rootStore={rootStore} />
                            <S.Main sidebarOpened={!rootStore.layoutStore.sidebarCollapsed}>
                                <Routes>
                                    <Route path="/" element={<Navigate to="/claims" replace />} />
                                    <Route path="/claims" element={<ClaimListing rootStore={rootStore} />} />
                                    <Route path="/claims/create" element={<ClaimEditor />} />
                                    <Route path="/claims/:claimId/edit" element={<ClaimEditor />} />
                                    <Route path="/claims/:claimId" element={<ClaimDetail rootStore={rootStore} />} />
                                    <Route path="/claims/by-:filterType/:id" element={<ByFilter rootStore={rootStore} />} />
                                    <Route path="/messaging/*" element={<MessagingOutlet rootStore={rootStore} />} />
                                    <Route path="/eligibility" element={<Eligibility />} />
                                    <Route path="/reports" element={<Reports rootStore={rootStore} />} />
                                    <Route path="/settings/*" element={<Settings />} />
                                    <Route path="/chargemasters" element={<ChargemasterListing />} />
                                    <Route
                                        path="/chargemasters/:chargemasterId"
                                        element={<ChargemasterDetail />}
                                    />
                                    <Route path="/payments" element={<Payments />} />
                                    <Route path="/overpayments" element={<Navigate to="/overpayments/pending" replace />} />
                                    <Route path="/overpayments/:tab" element={<Overpayments />} />
                                    <Route path="/patients/:patientId/statement" element={<StatementDetails />} />
                                    <Route path="/documents" element={<Documents />} />
                                    <Route path="/guarantors" element={<Guarantors />} />
                                    <Route path="/guarantors/:id" element={<GuarantorDetails />} />
                                    <Route path="/transactions" element={<Transactions />} />
                                    <Route path="/mail" element={<Navigate to="/mail/inbox" replace />} />
                                    <Route path="/mail/:tab" element={<Mail />} />
                                    <Route path="/phone" element={<Phone />} />

                                    <Route path="/fax" element={<Navigate to="/fax/inbox" replace />} />
                                    <Route path="/fax/:tab" element={<Fax />} />
                                    <Route path="/verifications/experiments/*" element={<ExperimentsOutlet />} />
                                    <Route path="/verifications/evidence/*" element={<EvidenceOutlet />} />

                                    {/* <Route path="/mail" element={<RestrictedRoute requires="DEV"><Mail /></RestrictedRoute>} /> */}
                                    <Route
                                        path="/settlements"
                                        element={
                                            <RestrictedRoute requires="ADMIN">
                                                <Settlements />
                                            </RestrictedRoute>
                                        }
                                    />
                                    <Route path="*" element={renderNotFound()} />
                                </Routes>
                            </S.Main>
                            {!rootStore.isLocal() && <InactivityDetector />}
                        </ProviderMessagingProvider>
                    </DialogProvider>
                </ToastProvider>
            );
        },
    ),
);
